import React, { useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { HelpCircle } from "lucide-react";

import { Input, Label, Button } from "components/ui";
import {
  AddHelpAndFeedbackAction,
  UpdateHelpAndFeedbackAction,
} from "store/middlewares/dataManagement/helpAndFeedback";
import { InputTextarea, SubmitLoaderButton } from "components/atoms";
import { closeAdminModal } from "store/slices/common/adminModal.slice";

function ModalEditHelpAndFeedback({ row, filter }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);

  const handleSubmit = (values) => {
    const updateValue = {
      ...values,
      title: values.title.trim(),
      onSuccess: () => formik.setSubmitting(false),
      onError: () => setFormError(true),
    };

    if (row) {
      dispatch(
        UpdateHelpAndFeedbackAction({
          id: row.id,
          ...updateValue,
        }),
      );
    } else {
      dispatch(AddHelpAndFeedbackAction({ ...updateValue, filter }));
    }
  };

  const formik = useFormik({
    initialValues: {
      title: row?.title ? row?.title : "",
      content: row?.content ? row?.content : "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      content: Yup.string().required("Content is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <HelpCircle className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit help and feedback" : "Add help and feedback"}
        </h2>
      </div>

      {/* Inputs (Stacked label + input) */}
      <div className="grid gap-4">
        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <Label>Title</Label>
          <Input
            error={formik.errors.title && formik.touched.title}
            errorMsg={formik.errors.title}
            value={formik.values.title}
            type="text"
            name="title"
            maxLength={50}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isSpace={true}
            placeholder="Enter title"
            className="w-full"
            variant="lg"
          />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-1.5">
          <Label>Content</Label>
          <InputTextarea
            textareaClassName="rounded-lg w-full px-3 py-2 no-scrollbar"
            error={formik.errors.content && formik.touched.content}
            errorMsg={formik.errors.content}
            value={formik.values.content}
            type="text"
            isRequired={false}
            name="content"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isSpace={true}
            placeholder="Enter content"
            className="w-full"
            maxLength={250}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditHelpAndFeedback;